
import { LegacyRef, useEffect, useMemo, useRef, useState } from 'react';
import { useXTerm } from 'react-xtermjs';
import { FitAddon } from '@xterm/addon-fit';
import { Readline as ReadlineAddon } from 'xterm-readline';
import * as monaco from 'monaco-editor';
import { TToken } from '../../elf-core/ElfTokenizer';
import { applyTokensAsDecorations } from './highlight';
import { TElfLanguageInstructionsMetadata, TElfLanguageKeywordsMetadata } from '../../elf-core/ElfAst';
import * as astCtx from '../../elf-core/ElfDefaultAstContext';
import { compileRuntime, ElfCallStack, ElfCodeMap, ElfCodeMapExecutableStepExt, ElfCpuCycles, ElfEvalStepValue, ElfHeap, ElfRegisters, ElfRuntimeLib, ElfStack, ElfStepMetadata } from '../../elf-core/ElfRuntime';
import { BreakpointsContainer, TLineColState } from './BreakpointsContainer';
import { compileElfCode, TElfUserCompilationResultSuccess } from './ElfEditorTools';
import { ElfRepl, TReplDefaults } from './ElfRepl';
import './index.css';

function btnStyle(bg: string, disabled?: boolean) {
	return {
		background: bg,
		color: 'white',
		padding: '0.25rem 1rem',
		borderRadius: '4px',
		border: 'none',
		cursor: disabled ? 'not-allowed' : 'pointer',
		opacity: disabled ? 0.5 : 1,
	};
}

function useElfStudioButtonStates() {
	const [, setVersion] = useState(0);

	const obj = useMemo(() => {
		let releaseRunning = false;
		let debugRunning = false;
		let buildSucceeded = false;

		return {
			setBuildSucceeded(succeeded: boolean) {
				const diff = buildSucceeded !== succeeded;

				buildSucceeded = succeeded;
				if (diff) setVersion((v) => v + 1);
			},
			setRelease(running: boolean) {
				const diff = releaseRunning !== running;

				releaseRunning = running;
				if (diff) setVersion((v) => v + 1);
			},
			setDebug(running: boolean) {
				const diff = debugRunning !== running;

				debugRunning = running;
				if (diff) setVersion((v) => v + 1);
			},
			releaseDisabled() {
				return (releaseRunning || debugRunning) || !buildSucceeded;
			},
			debugDisabled() {
				return (debugRunning || releaseRunning) || !buildSucceeded;
			},
			debugMenuDisabled() {
				return (!debugRunning) || !buildSucceeded;
			},
			codeChangesDisabled() {
				return (releaseRunning || debugRunning);
			},
		};
	}, []);

	return obj;
}

function runStateButton(action: () => any, toggler: (running: boolean) => any) {
	return () => {
		(async () => {
			try {
				toggler(true);
				// let components refresh.
				await new Promise((r) => setTimeout(r, 0));
				await action();
			} finally {
				toggler(false);
			}
		})();
	};
}

function editorCommandToggleBreakpoint(editorCtx: MonacoContext | null): void {
	if (!editorCtx) return;
	editorCtx.breakpointsContainer.toggleBreakpoint(editorCtx.editor.getPosition()?.lineNumber, true);
}

function editorCommandTerminateDebugger(editorCtx: MonacoContext | null): void {
	if (!editorCtx || editorCtx.uiButtons?.debugMenuDisabled()) return;
	editorCtx.breakpointsContainer.terminateDebugger();
}

function editorCommandDebuggerStepOver(editorCtx: MonacoContext | null): void {
	if (!editorCtx || editorCtx.uiButtons?.debugMenuDisabled()) return;
	editorCtx.breakpointsContainer.resolveNextStep('instruction');
}

function editorCommandDebuggerStepIn(editorCtx: MonacoContext | null): void {
	if (!editorCtx || editorCtx.uiButtons?.debugMenuDisabled()) return;
	editorCtx.breakpointsContainer.resolveNextStep('any');
}

function editorCommandDebuggerContinue(editorCtx: MonacoContext | null): void {
	if (!editorCtx || editorCtx.uiButtons?.debugMenuDisabled()) return;
	editorCtx.breakpointsContainer.resolveNextStep('breakpoint');
}

function editorCommandDebuggerRunProgram(editorCtx: MonacoContext | null): void {
	if (!editorCtx || editorCtx.uiButtons?.debugDisabled()) return;
	if (editorCtx.uiButtons && editorCtx.currentTranspiled) {
		const runner = runStateButton(() => runProgram(editorCtx.currentTranspiled, editorCtx, { debug: true }), (running) => editorCtx.uiButtons?.setDebug(running));

		runner();
	}
}

function editorCommandDebuggerRunOrContinue(editorCtx: MonacoContext | null): void {
	if (!editorCtx || !editorCtx.uiButtons) return;
	if (!editorCtx.uiButtons.debugDisabled()) {
		editorCommandDebuggerRunProgram(editorCtx);
	} else if (!editorCtx.uiButtons.debugMenuDisabled()) {
		editorCommandDebuggerContinue(editorCtx);
	}
}

function editorCommandReleaseRunProgram(editorCtx: MonacoContext | null): void {
	if (!editorCtx || editorCtx.uiButtons?.releaseDisabled()) return;
	if (editorCtx.uiButtons && editorCtx.currentTranspiled) {
		const runner = runStateButton(() => runProgram(editorCtx.currentTranspiled, editorCtx, { debug: false }), (running) => editorCtx.uiButtons?.setRelease(running));

		runner();
	}
}

/* TODO: this must be done in background webworker.... se console logs when editor open for further instructions*/
/* To support bigger programs, we need to run tokenizer and compile ast separately, so this can be broken up in more steps*/
/* same thing like VS code does, where it runs "compile" after user stops typing. */
/* We could also further optimize this, to use regex highlighting (builtin and fast for monaco), and run tokenizer after user stops typing as well */
/* that way we only tokenize & compile after user releases the main thread */
function renderProgram(editor: monaco.editor.IStandaloneCodeEditor, ctx: MonacoContext): TElfUserCompilationResultSuccess | null {
	const code = editor.getValue();
	const compileResult = compileElfCode(code, { registers: ctx.elfRepl.envDefaults.registers });

	// populate type hints for editor.
	ctx.detectedUserLabels = {};
	compileResult.tokens.astReadyTokens.filter((t) => t.type === 'LABEL').forEach((labelToken) => {
		ctx.detectedUserLabels[labelToken.token] = true;
	});
	applyTokensAsDecorations(editor, ctx, compileResult.tokens.syntaxHighlightTokens, compileResult.errors, compileResult.warnings);

	return compileResult.compiled ? compileResult : null;
}

export type MonacoContext = {
	editor: monaco.editor.IStandaloneCodeEditor;
	elfRepl: ElfRepl;
	elfReplDefaults: TReplDefaults;
	breakpointsContainer: BreakpointsContainer;
	currentTranspiled: TElfUserCompilationResultSuccess | null;
	uiButtons: ReturnType<typeof useElfStudioButtonStates> | null;
	currentDecorations: string[]; // TODO: should be refactored into class
	detectedUserLabels: { [label: string]: true }; // TODO: should be refactored into class, probably be in same class as currentDecorations named: { languageParsingDecorations (tokens, characters, maybe ast? or ast on its own) }
};

const REPL_DEFAULTS: TReplDefaults = {
	registers: 8,
	stack: 8192,
	callStack: 128,
	cpuCycles: 2097152,
	heap: 1024 * 1024,
} as const;

function readReplDefaults() {
	const url = new URL(window.location.href);
	const searchParams = new URLSearchParams(url.search);
	let changed = false;

	// Apply defaults if keys are missing
	Object.entries(REPL_DEFAULTS).forEach(([key, defaultValue]) => {
		if (!searchParams.has(key)) {
			searchParams.set(key, defaultValue.toString());
			changed = true;
		}
	});

	if (changed) {
		const newUrl = `${url.pathname}?${searchParams.toString()}`;

		window.history.replaceState(null, '', newUrl);
	}

	// Set query state
	const queryObject: { [key: string]: number } = {};

	searchParams.forEach((value, key) => {
		queryObject[key] = Number(value);
	});

	return queryObject as TReplDefaults;
};

function saveFile(monacoContext: MonacoContext | null) {
	if (!monacoContext) return;
	const blob = new Blob([monacoContext.editor.getValue()], {
		type: 'text/plain',
	});
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');

	a.href = url;
	a.download = 'program.elf';
	a.click();
	URL.revokeObjectURL(url);
}

async function runProgram(compileResult: TElfUserCompilationResultSuccess | null, monacoContext: MonacoContext | null, programOpts: { debug: boolean }): Promise<void> {
	if (!compileResult || !monacoContext) return;
	monacoContext.elfRepl.scrollToBottom();

	const runtime = compileRuntime(compileResult.ast.tree, compileResult.ast.treeCtx, { debug: programOpts.debug });
	const tokens = compileResult.tokens.astReadyTokens;

	const tokenLookup = new Map<string, TToken>();

	tokens.forEach((t) => tokenLookup.set(`${t.line}_${t.column}`, t));

	const runtimeLib = new ElfRuntimeLib();

	runtimeLib.print = (...args: number[]) => {
		monacoContext.elfRepl.write(args.join(' '));
	};
	runtimeLib.printText = (...args: number[]) => {
		monacoContext.elfRepl.write(args.map((arg) => String.fromCharCode(arg)).join(''));
	};
	runtimeLib.prompt = (str: string) => prompt(str) ?? '';

	runtimeLib.debuggerPrompt = async (str: string): Promise<string> => {
		return await monacoContext.elfRepl.readlineCancellationPrompt(str, () => monacoContext.breakpointsContainer.isDebuggerTerminated());
	};

	// should be use ref as ctx in a class, if something crashes and we lose decorations context, then we can always clean up here first (past decorations)!
	let debuggerDecorations: string[] = [];
	const currentModel = monacoContext.editor.getModel()!;
	const globalEndLine = currentModel.getLineCount();
	const globalEndColumn = currentModel.getLineMaxColumn(globalEndLine);
	const registers = new ElfRegisters(monacoContext.elfRepl.envDefaults.registers);
	const stack = new ElfStack(monacoContext.elfRepl.envDefaults.stack);
	const callStack = new ElfCallStack(monacoContext.elfRepl.envDefaults.callStack);
	const cpuCycles = new ElfCpuCycles(monacoContext.elfRepl.envDefaults.cpuCycles);
	const heap = new ElfHeap(monacoContext.elfRepl.envDefaults.heap);

	const buildEvaluatedDisplayDecorations = (evaluatedValue: ElfEvalStepValue | null, line: number, column: number, endLine: number, endColumn: number) => {
		const debuggerColumnHighlightClass = evaluatedValue === null ? 'my-debugger-column-highlight' : 'my-debugger-column-highlight-evaluated';
		const evalValue = evaluatedValue?.evaluated ?? 'N/A';
		const displayEvalValue = Array.isArray(evalValue) ? '[binary]' : evalValue;

		const cs = callStack.getSize();
		const cc = cpuCycles.totalConsumed();
		const ss = stack.getSize();
		const ac = registers.getAccumulator();
		const stateStr = `{ accumulator:${ac}, cycles:${cc}, data_stack:${ss}, call_stack:${cs} }`;
		const newDecorations: monaco.editor.IModelDeltaDecoration[] = [
			{
				range: new monaco.Range(line, 1, line, 1),
				options: {
					isWholeLine: true,
					className: 'my-debugger-line-highlight',
				},
			},
			{
				range: new monaco.Range(line, column, endLine, endColumn),
				options: {
					className: debuggerColumnHighlightClass,
				},
			},
			{
				range: new monaco.Range(line, column, endLine, endColumn),
				options: {
					after: {
						content: `${evaluatedValue !== null ? ` = ${displayEvalValue} ` : ' '}${stateStr}`,
						inlineClassName: 'my-debugger-inline-value',
					},
				},
			},
		];

		return newDecorations;
	};

	runtimeLib.debuggerOnStep = async (codeMap: ElfCodeMap, executableStepExtArg: ElfCodeMapExecutableStepExt | null, evaluatedValue: ElfEvalStepValue | null, metadata: ElfStepMetadata | null): Promise<number> => {
		// evaluatedValue = if defined -> valuatedStep; else -> beforeEvaluationStep & null;
		debuggerDecorations = monacoContext.editor.deltaDecorations(debuggerDecorations, []);

		const isInstructionStep = Boolean(executableStepExtArg?.execUid);
		const breakpointContainer = monacoContext.breakpointsContainer;

		if (breakpointContainer.isDebuggerTerminated()) {
			return -1;
		}

		const lineColState: TLineColState = { line: codeMap.line, column: codeMap.column, callStack: callStack.getSize() };

		const shouldPauseHere = breakpointContainer.shouldPauseHere(lineColState, {
			isInstruction: isInstructionStep,
			isEvaluated: Boolean(evaluatedValue),
			isDebugPoint: Boolean(metadata?.debugHere),
		});

		if (!shouldPauseHere) {
			return 0;
		}

		// focus debug position
		monacoContext.editor.revealPosition({ lineNumber: lineColState.line, column: lineColState.column });

		if (isInstructionStep) { // executableStep = "complex" instruction step
			const { line, column } = codeMap;
			const endLine = executableStepExtArg!.endLine ?? globalEndLine;
			const endColumn = executableStepExtArg!.endColumn ?? globalEndColumn;
			const newDecorations: monaco.editor.IModelDeltaDecoration[] = buildEvaluatedDisplayDecorations(evaluatedValue, line, column, endLine, endColumn);

			debuggerDecorations = monacoContext.editor.deltaDecorations(debuggerDecorations, newDecorations);

		} else { // primitive instruction step
			const { line, column } = codeMap;
			const foundToken = tokenLookup.get(`${line}_${column}`);
			let endLine: number | null = null;
			let endColumn: number | null = null;

			if (executableStepExtArg) {
				// for now only for tree.type list (not really primitive but not worth a debugger step - so still kept primitive)
				endLine = executableStepExtArg!.endLine ?? globalEndLine;
				endColumn = executableStepExtArg!.endColumn ?? globalEndColumn;
			} else {
				// this can be removed once all TSyntaxTree entries have endline implemented.
				endLine = (foundToken?.lastLine ?? line);
				endColumn = (foundToken?.lastColumn ?? column) + 1;
			}

			const newDecorations: monaco.editor.IModelDeltaDecoration[] = buildEvaluatedDisplayDecorations(evaluatedValue, line, column, endLine, endColumn);

			debuggerDecorations = monacoContext.editor.deltaDecorations(debuggerDecorations, newDecorations);
		}

		const repl = monacoContext.elfRepl;
		const dbgRuntimeLib = new ElfRuntimeLib();

		dbgRuntimeLib.print = runtimeLib.print;
		dbgRuntimeLib.printText = runtimeLib.printText;
		dbgRuntimeLib.prompt = runtimeLib.prompt;
		dbgRuntimeLib.debuggerPrompt = runtimeLib.debuggerPrompt;
		// eslint-disable-next-line require-await
		dbgRuntimeLib.debuggerOnStep = async () => 1;
		// this could potentially cause memory leak if previous await was not complete.
		// But lets keep it, as i dont want to write my own repl evaluator for now.
		// eslint-disable-next-line require-await
		async function debugReplLoop() {
			/**
	 * TODO: once we support multiple files, repl can merge functions and constants from elf code, so its callable in repl.
	 */
			repl.awaitDbgEval(lineColState.line, lineColState.column, evaluatedValue !== null, (input) => repl.exec(input, {
				callStack: callStack.clone(),
				cpuCycles: cpuCycles.clone(),
				heap: heap.clone(),
				registers: registers.clone(),
				stack: stack.clone(),
				runtimeLib: dbgRuntimeLib,
			}, { asDebug: true })).then(() => setTimeout(debugReplLoop));
		}

		// REPL overrides current cli line, thus give time to flush (what program wrote before breakpoint hit) so repl can check offset and add newline!
		await new Promise((r) => setTimeout(r, 0));
		debugReplLoop();
		await breakpointContainer.awaitNextStep(lineColState);
		// goto next line so it doesn't write in debug repl.
		repl.writeln('');
		// now give time to flush new line for repl, else it could potentially write over current repl line
		await new Promise((r) => setTimeout(r, 0));

		return breakpointContainer.isDebuggerTerminated() ? -1 : 0;
	};

	const runtimeArgs = {
		registers,
		heap,
		stack,
		callStack,
		cpuCycles,
		runtimeLib,
	};
	const elfRepl = monacoContext.elfRepl;

	elfRepl.writeln('');
	// if debug -> debug.elf else release.elf
	const isDebug = programOpts.debug;

	elfRepl.writeAsUserCommand(`./${isDebug ? 'debug' : 'release'}.elf --registers=${monacoContext.elfRepl.envDefaults.registers} --heap=${monacoContext.elfRepl.envDefaults.heap} --stack=${monacoContext.elfRepl.envDefaults.stack} --callstack=${monacoContext.elfRepl.envDefaults.callStack} --cycles=${monacoContext.elfRepl.envDefaults.cpuCycles}`);

	await elfRepl.runElfJsRuntime(runtime, runtimeArgs, { printSuccess: true, elfCode: compileResult.elfCode });
	debuggerDecorations = monacoContext.editor.deltaDecorations(debuggerDecorations, []); // TODO: and all other decorations, cleanup method..
	monacoContext.breakpointsContainer.prepareForNextExecution();

	// REQUIRED as its a race condition of debugEval just resolving - causing the terminal repl to bug out.
	await new Promise((r) => setTimeout(r, 100));
	startElfEvalLoop(elfRepl, monacoContext.elfReplDefaults);
}

function startElfEvalLoop(elfRepl: ElfRepl, replDefaults: TReplDefaults) {
	const regConf = replDefaults.registers;
	const heapConf = replDefaults.heap;
	const stackConf = replDefaults.stack;
	const callStackConf = replDefaults.callStack;
	const cyclesConf = replDefaults.cpuCycles;

	elfRepl.writeAsUserCommand(`./eval.elf --registers=${regConf} --heap=${heapConf} --stack=${stackConf} --callstack=${callStackConf} --cycles=${cyclesConf}`);

	const registers = new ElfRegisters(regConf);
	const stack = new ElfStack(stackConf);
	const callStack = new ElfCallStack(callStackConf);
	const cpuCycles = new ElfCpuCycles(cyclesConf);
	const heap = new ElfHeap(heapConf);
	const runtimeLib = new ElfRuntimeLib();

	runtimeLib.print = (...args: number[]) => {
		elfRepl.write(args.join(' '));
	};
	runtimeLib.printText = (...args: number[]) => {
		elfRepl.write(args.map((arg) => String.fromCharCode(arg)).join(''));
	};
	runtimeLib.prompt = (str: string) => prompt(str) ?? '';

	// eslint-disable-next-line require-await
	async function evalReplLoop() {
		elfRepl.awaitEval((input) => elfRepl.exec(input, {
			callStack: callStack.clone(),
			cpuCycles: cpuCycles.clone(),
			heap: heap.clone(),
			registers: registers.clone(),
			stack: stack.clone(),
			runtimeLib,
		})).then(() => setTimeout(evalReplLoop));
	}
	evalReplLoop();
}

export function MonacoEditor({ initialValue = null, useLocalStorage }: { initialValue?: string | null; useLocalStorage: boolean }) {
	const replSettingsQuery = useMemo(() => readReplDefaults(), []);
	const regConf = replSettingsQuery.registers;

	const editorRef = useRef<HTMLDivElement | null>(null);
	const disposablesRef = useRef<monaco.IDisposable[]>([]);
	const [currentTranspiled, setCurrentTranspiled] = useState<TElfUserCompilationResultSuccess | null>(null);
	const [editorCtx, setEditorCtx] = useState<MonacoContext | null>(null);
	const { instance, ref } = useXTerm(useMemo(() => ({
		options: {
			cursorBlink: true,
			cursorStyle: 'block',
			scrollOnUserInput: true,
			fontFamily: '"Fira Code", monospace',
			fontSize: 14,
			letterSpacing: 0,
			lineHeight: 1.2,
			convertEol: true,
			theme: {
				background: '#1e1e1e',
				foreground: '#cccccc',
				cursor: '#ffffff',
				black: '#1e1e1e',
				red: '#f44747',
				green: '#619955',
				yellow: '#ffaf00',
				blue: '#0a84ff',
				magenta: '#d670d6',
				cyan: '#4ec9b0',
				white: '#cccccc',
				brightBlack: '#2d2d2d',
				brightRed: '#ff0000ff',
				brightGreen: '#41ec1bff',
				brightYellow: '#ffaf00',
				brightBlue: '#0a84ff',
				brightMagenta: '#d670d6',
				brightCyan: '#4ec9b0',
				brightWhite: '#ffffff',
			},
		},
	}), []));

	useEffect(() => {
		if (!instance || !editorCtx) return;

		const { elfRepl } = editorCtx;
		const fitAddon = new FitAddon();

		instance.loadAddon(fitAddon);
		fitAddon.fit();
		const readline = new ReadlineAddon();

		instance.loadAddon(readline);

		elfRepl.setTerminal(instance);
		elfRepl.setReadline(readline);

		startElfEvalLoop(elfRepl, replSettingsQuery);

		const ro = new ResizeObserver(() => fitAddon.fit());

		ro.observe(instance.element!.parentElement!);

		return () => ro.disconnect();
		// for now until cleanup we need to ignore startElfEvalLoop dependency (causing rerender).
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [instance, editorCtx]);

	useEffect(() => {
		if (!editorRef.current) return;
		// eslint-disable-next-line no-console
		console.log('EDITOR CREATING!');
		const astBuildCtx = astCtx.build({ maxNumberOfRegisters: regConf });

		function mapElfLanguageReservedKeywordFunction(type: 'KEYWORD' | 'FUNCTION', keyword: string, metadata: TElfLanguageKeywordsMetadata | TElfLanguageInstructionsMetadata) {
			const example = ` \`\` ${metadata.example} \`\` `;
			const description = metadata.description;
			const cycles = ` \`\` ${metadata.cyclesDescription} \`\` `;
			const modifiesAccumulator = metadata.modifiesAccumulator;
			const returnsAccumulator = 'returnsAccumulator' in metadata ? metadata.returnsAccumulator : null;
			const accumulatorDesc = `\`\` spreminja=${modifiesAccumulator ? 'DA' : 'NE'} \`\` ${returnsAccumulator !== null ? `\`\`vrača=${returnsAccumulator ? 'DA' : 'NE'} \`\` ` : ''}`;

			return {
				type,
				name: keyword,
				modifiesAccumulator,
				returnsAccumulator,
				args: metadata.arguments.length,
				arguments: metadata.arguments,
				description,
				example,
				cycles,
				invocation: `${keyword} ${metadata.arguments.map((arg) => `\`${arg.map((a) => `[${a.argName}:${a.argToken}]`).join('|')}\``).join(' ')}`,
				plainTextHint: `${metadata.arguments.map((arg) => `[${Array.from(new Set(arg.map((a) => a.argName))).map((name) => `${name}`).join('|')}]`).join(' ')}`,
				plainTextHintDescription: { value: `${description}  \n  \nPrimer: ${example}  \n  \nCikli: ${cycles}  \n  \nAkumulator: ${accumulatorDesc}`, isTrusted: true },
				argDescription: `${metadata.arguments.map((arg, i) =>
					`  - ${i + 1}. argument:  \n${arg.map((subArg) => `    * ${subArg.argName}  \n        - tip: ${subArg.argToken}  \n        - opis: ${subArg.argDescription}`).join('  \n')
					}  \n`,
				).join('  \n  \n')}`,
			};
		}

		// Needs to be added manually, as language parser doesn't prefix by ELSE, but by IF then consuming until optional ELSE is reached.
		const elseElfKeyword: ['ELSE', { metadata: TElfLanguageKeywordsMetadata }] = ['ELSE', {
			metadata: {
				cyclesDescription: '0',
				arguments: [],
				description: 'Opcijski del k stavku IF, ki se bo izvedel le, če je pogoj v stavku IF enak 0',
				example: 'IF 0 { (se ne izvede) ELSE { (se izvede) }',
				modifiesAccumulator: false,
			},
		}];
		const elfKeywords = [...Object.entries(astBuildCtx.languageContext.keywords), elseElfKeyword].map(([keyword, definition]) =>
			mapElfLanguageReservedKeywordFunction('KEYWORD', keyword, definition.metadata));

		const elfInstructions = Object.entries(astBuildCtx.languageContext.instructions as Required<typeof astBuildCtx.languageContext.instructions>).map(([keyword, definition]) =>
			mapElfLanguageReservedKeywordFunction('FUNCTION', keyword, definition!.metadata));
		const keywords = [
			...elfKeywords,
			...elfInstructions,
		];

		const hoverDisposable = monaco.languages.registerHoverProvider('plaintext', {
			provideHover: (model, position) => {
				const word = model.getWordAtPosition(position);

				if (!word) return null;

				const kw = keywords.find((k) => k.name === word.word);

				if (!kw) return null;

				return {
					range: new monaco.Range(
						position.lineNumber,
						word.startColumn,
						position.lineNumber,
						word.endColumn,
					),
					contents: [
						{ value: `**${kw.name}** (argumenti: ${kw.args})`, isTrusted: true },
						{ value: `***Definicija:***  \n${kw.invocation}`, isTrusted: true },
						{ value: `***Cikli:***  \n${kw.cycles}`, isTrusted: true },
						{ value: `***Opis:***  \n${kw.description}`, isTrusted: true },
						...(kw.modifiesAccumulator === null ? [] : [{ value: `***Spreminja akumulator:***  ${kw.modifiesAccumulator ? 'DA' : 'NE'}\n`, isTrusted: true }]),
						...(kw.returnsAccumulator === null ? [] : [{ value: `***Vrača akumulator:***  ${kw.returnsAccumulator ? 'DA' : 'NE'}\n`, isTrusted: true }]),
						{ value: `***Primer:***  \n${kw.example}`, isTrusted: true },
						{ value: `***Opis argumentov:***  \n${kw.argDescription}`, isTrusted: true },
					],
				};
			},
		});

		const completionDisposable = monaco.languages.registerCompletionItemProvider('plaintext', {
			triggerCharacters: ['#', '`', '('], // special characters for autocomplete
			provideCompletionItems: (model, position) => {

				const word = model.getWordUntilPosition(position);
				const prevChar = model.getValueInRange({
					startLineNumber: position.lineNumber,
					startColumn: Math.max(1, position.column - 1),
					endLineNumber: position.lineNumber,
					endColumn: position.column,
				});

				const range = {
					startLineNumber: position.lineNumber,
					endLineNumber: position.lineNumber,
					startColumn: word.startColumn,
					endColumn: word.endColumn,
				};

				const kwMap: { [key: string]: monaco.languages.CompletionItemKind } = {
					'KEYWORD': monaco.languages.CompletionItemKind.Keyword,
					'FUNCTION': monaco.languages.CompletionItemKind.Function,
				};

				if (prevChar === '#') {
					const range = new monaco.Range(position.lineNumber, position.column - 1, position.lineNumber, position.column);

					return {
						suggestions: [
							{
								label: '#` `', // shows in the suggestion list
								kind: monaco.languages.CompletionItemKind.Snippet, // treat as snippet
								insertText: '#`$0`', // $0 = cursor position
								insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
								documentation: 'ascii znak',
								range, // replaces the correct portion (e.g., existing #)
							},
						],
					};
				}

				if (prevChar === '`') {
					const range = new monaco.Range(position.lineNumber, position.column - 1, position.lineNumber, position.column);

					return {
						suggestions: [
							{
								label: '` `',
								kind: monaco.languages.CompletionItemKind.Snippet,
								insertText: '`$0`',
								insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
								documentation: 'niz ascii znakov',
								range,
							},
						],
					};
				}

				if (prevChar === '(') {
					const range = new monaco.Range(position.lineNumber, position.column - 1, position.lineNumber, position.column);

					return {
						suggestions: [
							{
								label: '`(večvrstični komentar)`',
								kind: monaco.languages.CompletionItemKind.Snippet,
								insertText: '(\n $0\n', // (IDE automatically adds ")")
								insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
								documentation: '(večvrstični komentar)',
								range,
							},
						],
					};
				}

				const suggestionsBuiltin = keywords.map((kw): monaco.languages.CompletionItem => ({
					label: kw.name,
					kind: kwMap[kw.type]!,
					insertText: kw.name,
					detail: kw.plainTextHint,
					documentation: kw.plainTextHintDescription,
					range,
				}));

				// User typed in a reserved KEYWORD
				if (/^[A-Z_\d]+$/.test(word.word)) {
					return { suggestions: suggestionsBuiltin };
				}

				const suggestionsLabels = Object.keys(editorCtx.detectedUserLabels).map((userLabel: string): monaco.languages.CompletionItem => ({
					label: userLabel,
					kind: monaco.languages.CompletionItemKind.Constant,
					insertText: userLabel,
					range,
				}));

				// User typed in a label
				if (/^[a-z_\d]+$/.test(word.word)) {
					if (suggestionsLabels.length) {
						return { suggestions: suggestionsLabels };
					}
				}

				// User has cursor on empty space - nothing to predict upon. Show options:
				if (word.word === '') {
					const sortWeight = new Map<monaco.languages.CompletionItemKind, number>();

					sortWeight.set(monaco.languages.CompletionItemKind.Keyword, 1);
					sortWeight.set(monaco.languages.CompletionItemKind.Function, 2);
					sortWeight.set(monaco.languages.CompletionItemKind.Constant, 3);
					const maxWeight = 9;

					return {
						suggestions: [...suggestionsBuiltin, ...suggestionsLabels].map((s) => {
							s.sortText = `${sortWeight.get(s.kind) ?? maxWeight}${s.insertText}`;

							return s;
						}),
					};
				}

				// Not empty & not label & not keyword
				// fallback to recommend any word found in editor.
				const text = model.getValue();
				const regex = /\b\w+\b/g;
				const existingWords = new Set<string>();
				let match;

				while ((match = regex.exec(text)) !== null) {
					const w = match[0];

					existingWords.add(w);
				}

				const tokenSuggestions = Array.from(existingWords)
					.filter((w) => w.startsWith(word.word))
					.map((w) => ({
						label: w,
						kind: monaco.languages.CompletionItemKind.Text,
						insertText: w,
						range,
					}));

				return { suggestions: tokenSuggestions };

			},
		});

		let saveLocalStorageTimeout: ReturnType<typeof setTimeout> | null = null;

		function conditionallySaveEditorToLocalStorage(): void {
			if (!useLocalStorage) {
				return;
			}
			if (saveLocalStorageTimeout !== null) {
				clearTimeout(saveLocalStorageTimeout);
				saveLocalStorageTimeout = null;
			}
			saveLocalStorageTimeout = setTimeout(() => {
				// eslint-disable-next-line no-console
				console.log('Saving to local storage...');
				localStorage.setItem('ELF_STUDIO_CODE', editor.getValue());
			}, 500);
		}
		function conditionallyGetEditorLocalStorage(): string {
			if (!useLocalStorage) return '';

			return localStorage.getItem('ELF_STUDIO_CODE') ?? '';
		}

		const editor = monaco.editor.create(editorRef.current, {
			value: initialValue ?? conditionallyGetEditorLocalStorage(),
			language: 'plaintext',
			theme: 'vs-dark',
			automaticLayout: true,
			fontSize: 16,
			glyphMargin: true,
			lineNumbersMinChars: 2,
			// TODO: minimap extended coloring can be added in deltaDecorations object { options: { minimap: {...} } }.
		});

		const editorCtx: MonacoContext = {
			currentDecorations: [],
			breakpointsContainer: new BreakpointsContainer(editor),
			elfRepl: new ElfRepl(replSettingsQuery),
			elfReplDefaults: replSettingsQuery,
			detectedUserLabels: {},
			editor,
			currentTranspiled: null,
			uiButtons: null,
		};

		const resizeObserver = new ResizeObserver(() => editor.layout());

		resizeObserver.observe(editorRef.current!);

		editor.getModel()!.setEOL(monaco.editor.EndOfLineSequence.LF);

		editor.addCommand(
			monaco.KeyCode.F4,
			() => editorCommandReleaseRunProgram(editorCtx),
		);

		editor.addCommand(
			monaco.KeyCode.F5,
			() => editorCommandDebuggerRunOrContinue(editorCtx),
		);

		editor.addCommand(
			monaco.KeyMod.Shift | monaco.KeyCode.F5,
			() => editorCommandTerminateDebugger(editorCtx),
		);

		editor.addCommand(
			monaco.KeyCode.F9,
			() => editorCommandToggleBreakpoint(editorCtx),
		);

		editor.addCommand(
			monaco.KeyCode.F10,
			() => editorCommandDebuggerStepOver(editorCtx),
		);

		editor.addCommand(
			monaco.KeyCode.F11,
			() => editorCommandDebuggerStepIn(editorCtx),
		);

		editor.addCommand(
			monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyP,
			() => editor.trigger('keyboard', 'editor.action.gotoLine', null),
		);

		editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS,
			() => saveFile(editorCtx),
		);

		editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyK, () => {
			if (editor.getRawOptions().readOnly) {
				// hack to trigger message that editor is in readonly mode.
				(editor.getContribution('editor.contrib.readOnlyMessageController') as any)._onDidAttemptReadOnlyEdit();

				return;
			}

			const model = editor.getModel();

			if (!model) return;

			const selection = editor.getSelection();

			if (!selection) return;

			const startLine = selection.startLineNumber;
			const endLine = selection.endLineNumber;

			const lines = [];

			for (let i = startLine; i <= endLine; i++) {
				lines.push(model.getLineContent(i));
			}

			const allPrefixed = lines.every((line) => line.startsWith('~'));

			const updated = lines.map((line) => {
				if (allPrefixed) {
					return line.slice(1);
				} else {
					return `~${line}`;
				}
			});

			const range = new monaco.Range(startLine, 1, endLine, model.getLineMaxColumn(endLine));
			const edit = {
				range,
				text: updated.join('\n'),
			};

			model.pushEditOperations([], [edit], () => null);
		});

		// hack to show details of suggestions by default.
		const suggestController = editor.getContribution('editor.contrib.suggestController');

		if ((suggestController as any).widget) {
			const suggestWidget = (suggestController as any).widget.value;

			if (suggestWidget?._setDetailsVisible) {
				// if user switch them off (by pressing x), they will not be visible until
				// the user switches them back on (by pressing >).
				suggestWidget._setDetailsVisible(true);
			}
		}

		editor.onMouseDown((e) => editorCtx.breakpointsContainer.onMouseDown(e, true));
		editor.onMouseMove((e) => editorCtx.breakpointsContainer.onMouseMove(e, true));

		// Highlight on initial load
		setCurrentTranspiled(renderProgram(editor, editorCtx));
		setEditorCtx(editorCtx);

		// Highlight on content change
		const onDidChangeDisposable = editor.onDidChangeModelContent((e) => {
			conditionallySaveEditorToLocalStorage();
			e.changes.forEach((change) => {
				editorCtx.breakpointsContainer.onEditorChange(change, false);
			});
			editorCtx.breakpointsContainer.render();
			setCurrentTranspiled(renderProgram(editor, editorCtx));
		});

		disposablesRef.current.push(hoverDisposable, completionDisposable, onDidChangeDisposable);

		return () => {
			resizeObserver.disconnect();
			// for now until cleanup we need to ignore as ref wont change by the time this effect cleans up (as stated by react)
			// eslint-disable-next-line react-hooks/exhaustive-deps
			disposablesRef.current.forEach((d) => d.dispose());
			editor.dispose();
		};
		// for now until cleanup we need to ignore 'regConf', 'replSettingsQuery', and 'useLocalStorage' all those have infinite lifespan.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialValue, setCurrentTranspiled]);

	const uiButtons = useElfStudioButtonStates();

	if (editorCtx) {
		editorCtx.currentTranspiled = currentTranspiled;
		editorCtx.uiButtons = uiButtons;
		editorCtx.editor.updateOptions({ readOnly: uiButtons.codeChangesDisabled() });
	}

	uiButtons.setBuildSucceeded(currentTranspiled !== null);

	const codeContainerRef = useRef<HTMLDivElement | null>(null);
	const terminalContainerRef = useRef<HTMLDivElement | null>(null);
	const [isTerminalDragging, setIsTerminalDragging] = useState(false);

	const resizeTerminalStartDrag = (e: any) => {
		e.preventDefault();
		setIsTerminalDragging(true);
	};

	const resizeTerminalStopDragging = () => setIsTerminalDragging(false);

	const resizeTerminalOnMouseMove = (e: any) => {
		if (!isTerminalDragging) return;
		const containerRect = codeContainerRef.current!.getBoundingClientRect();
		const newHeight = containerRect.bottom - e.clientY;

		terminalContainerRef.current!.style.height = `${newHeight}px`;
	};

	return (
		<div
			ref={codeContainerRef}
			onMouseMove={resizeTerminalOnMouseMove}
			onMouseUp={resizeTerminalStopDragging}
			style={{
				width: '100%',
				height: '100vh',
				display: 'flex',
				flexDirection: 'column',
				background: '#1e1e1e',
				color: '#ddd',
				fontFamily: '"Fira Code", monospace',
				overflow: 'hidden',
			}}
		>
			{/* Top Controls */}
			<div
				style={{
					display: 'flex',
					gap: '0.5rem',
					padding: '0.5rem',
					background: '#262626',
					borderBottom: '1px solid #333',
					alignItems: 'center',
					justifyContent: 'center',
				}}
			>
				{/* Left Save Icon */}
				<button
					style={{
						position: 'absolute',
						left: '0.5rem',
						background: 'transparent',
						border: 'none',
						cursor: 'pointer',
						color: 'white',
						fontSize: '1rem',
					}}
					onClick={() => saveFile(editorCtx)}
				>
					💾
				</button>
				<label>ELF Studio</label>
			</div>

			{/* Editor fills available space */}
			<div
				ref={editorRef}
				style={{
					flex: 1,
					minHeight: 0,
					background: '#1e1e1e',
					overflow: 'hidden',
				}}
			/>

			{ /* resize terminal horizontal border */}
			<div
				onMouseDown={resizeTerminalStartDrag}
				style={{
					height: '5px',
					cursor: 'ns-resize',
					background: '#333',
				}}
			/>

			{/* Bottom Controls */}
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between', // razdeli leve in desne gumbe
					gap: '0.5rem',
					padding: '0.5rem',
					background: '#262626',
					borderTop: '1px solid #333',
				}}
			>
				{/* Release menu */}
				<div style={{ display: 'flex', gap: '0.5rem' }}>
					<button
						style={btnStyle('#007acc', uiButtons.releaseDisabled())}
						disabled={uiButtons.releaseDisabled()}
						onClick={() => editorCommandReleaseRunProgram(editorCtx)}
					>
						Run release ▶▶▶
					</button>
				</div>

				{/* Debug menu */}
				<div style={{ display: 'flex', gap: '0.5rem' }}>
					<button
						style={btnStyle('#007acc', uiButtons.debugDisabled())}
						disabled={uiButtons.debugDisabled()}
						onClick={() => editorCommandDebuggerRunProgram(editorCtx)}
					>
						Run debug ▶
					</button>
					<button
						onClick={() => editorCommandDebuggerContinue(editorCtx)}
						style={btnStyle('#007acc', uiButtons.debugMenuDisabled())}
					>
						Continue ●
					</button>
					<button
						onClick={() => editorCommandDebuggerStepOver(editorCtx)}
						style={btnStyle('rgba(108, 162, 7, 1)', uiButtons.debugMenuDisabled())}
					>
						Step →
					</button>
					<button
						onClick={() => editorCommandDebuggerStepIn(editorCtx)}
						style={btnStyle('rgba(221, 147, 0, 1)', uiButtons.debugMenuDisabled())}
					>
						Step ↳
					</button>
					<button
						onClick={() => editorCommandTerminateDebugger(editorCtx)}
						style={btnStyle('rgba(204, 31, 0, 1)', uiButtons.debugMenuDisabled())}
					>
						Stop ■
					</button>
				</div>
			</div>

			{/* Terminal */}
			<div
				ref={terminalContainerRef}
				style={{
					height: '300px',
					padding: '0.5rem',
					background: '#262626',
					borderTop: '1px solid #333',
				}}
			>
				<div
					style={{
						overflow: 'hidden',
						height: '100%',
						width: '100%',
						background: '#111',
						border: '1px solid #333',
						borderRadius: '6px',
					}}
				>
					<div ref={ref as unknown as LegacyRef<HTMLDivElement>} style={{ height: '100%', width: '100%', background: '#1e1e1e' }} />
				</div>
			</div>

		</div >
	);
}
